import React, { useState, useEffect } from 'react';
import { 
  DollarSign, ShoppingCart, Package, MousePointerClick, 
  Plus, X, Activity, BarChart3, RefreshCw, Clock, Search, Trophy, TrendingUp 
} from 'lucide-react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import api from '../services/api';
import './Dashboard.css';

const Dashboard = ({ estabelecimentoId }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ grafico: [], grafico_pedidos: [], grafico_produtos: [], cards: {} });
  const [showWidgetMenu, setShowWidgetMenu] = useState(false);
  
  const [widgetsAtivos, setWidgetsAtivos] = useState(() => {
    const salvos = localStorage.getItem(`widgets_${estabelecimentoId}`);
    return salvos ? JSON.parse(salvos) : ['faturamento', 'pedidos_dia', 'grafico_vendas', 'grafico_evolucao', 'grafico_produtos_barras'];
  });

  const templates = {
    faturamento: { id: 'faturamento', title: 'Faturamento Mensal', icon: <DollarSign size={20}/>, type: 'card', color: 'purple' },
    pedidos_dia: { id: 'pedidos_dia', title: 'Pedidos Hoje', icon: <ShoppingCart size={20}/>, type: 'card', color: 'red' },
    produtos_total: { id: 'produtos_total', title: 'Total Produtos', icon: <Package size={20}/>, type: 'card', color: 'orange' },
    cliques: { id: 'cliques', title: 'Cliques no Cardápio', icon: <MousePointerClick size={20}/>, type: 'card', color: 'blue' },
    horario_pico: { id: 'horario_pico', title: 'Horário de Pico (Cliques)', icon: <Clock size={20}/>, type: 'card', color: 'blue' },
    produto_pesquisado: { id: 'produto_pesquisado', title: 'Mais Pesquisado', icon: <Search size={20}/>, type: 'card', color: 'orange' },
    produto_clicado: { id: 'produto_clicado', title: 'Produto Mais Clicado', icon: <MousePointerClick size={20}/>, type: 'card', color: 'purple' },
    produto_vendido: { id: 'produto_vendido', title: 'Produto Mais Vendido', icon: <Trophy size={20}/>, type: 'card', color: 'red' },
    grafico_vendas: { id: 'grafico_vendas', title: 'Fluxo de Faturamento (Área)', type: 'chart_area' },
    grafico_evolucao: { id: 'grafico_evolucao', title: 'Tendência de Vendas (Linha)', type: 'chart_line' },
    grafico_pedidos: { id: 'grafico_pedidos', title: 'Volume de Pedidos (Colunas)', type: 'chart_bar' },
    grafico_produtos_barras: { id: 'grafico_produtos_barras', title: 'Desempenho de Produtos (Barras)', type: 'chart_bar_produtos' }
  };

  const carregarStats = async () => {
    try {
      const res = await api.get(`/cardapio/dashboard-stats/${estabelecimentoId}`);
      setStats(res.data);
    } catch (e) {
      console.error("Erro no BI:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (estabelecimentoId) {
      carregarStats();
    }
    localStorage.setItem(`widgets_${estabelecimentoId}`, JSON.stringify(widgetsAtivos));
  }, [estabelecimentoId, widgetsAtivos]);

  const renderWidget = (id) => {
    const w = templates[id];
    if (!w) return null;

    if (w.type === 'card') {
      let valor = '-';
      if (id === 'faturamento') {
        valor = `R$ ${Number(stats.cards?.faturamento_mensal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
      } else if (id === 'pedidos_dia') {
        valor = stats.cards?.pedidos_hoje || 0;
      } else if (id === 'cliques') {
        valor = stats.cards?.cliques || 0;
      } else if (id === 'produtos_total') {
        valor = stats.cards?.total_produtos || 0;
      } else if (id === 'horario_pico') {
        valor = stats.cards?.horario_pico || '19:00 - 22:00';
      } else if (id === 'produto_pesquisado') {
        valor = stats.cards?.produto_mais_pesquisado || 'Nenhum';
      } else if (id === 'produto_clicado') {
        valor = stats.cards?.produto_mais_clicado || 'Nenhum';
      } else if (id === 'produto_vendido') {
        valor = stats.cards?.produto_mais_vendido || 'Nenhum';
      }

      return (
        <div key={id} className={`stat-card-v2 ${w.color} animate-pop-in`}>
          <button className="btn-remove-widget" onClick={() => setWidgetsAtivos(widgetsAtivos.filter(x => x !== id))}><X size={14}/></button>
          <div className="card-icon">{w.icon}</div>
          <div className="card-data">
            <span className="label">{w.title}</span>
            <p className="value" style={{ fontSize: id.includes('produto') || id === 'horario_pico' ? '15px' : 'inherit' }}>{valor}</p>
          </div>
        </div>
      );
    }

    if (w.type === 'chart_area') {
      return (
        <div key={id} className="chart-box full-width animate-pop-in">
          <button className="btn-remove-widget" onClick={() => setWidgetsAtivos(widgetsAtivos.filter(x => x !== id))}><X size={14}/></button>
          <div className="chart-header"><h3><Activity size={18} color="#EA1D2C" /> {w.title}</h3></div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.grafico}>
                <defs>
                  <linearGradient id="colorFat" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EA1D2C" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#EA1D2C" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12}} />
                <YAxis hide />
                <Tooltip />
                <Area type="monotone" dataKey="faturamento" stroke="#EA1D2C" strokeWidth={3} fill="url(#colorFat)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    if (w.type === 'chart_line') {
      return (
        <div key={id} className="chart-box full-width animate-pop-in">
          <button className="btn-remove-widget" onClick={() => setWidgetsAtivos(widgetsAtivos.filter(x => x !== id))}><X size={14}/></button>
          <div className="chart-header"><h3><TrendingUp size={18} color="#2563eb" /> {w.title}</h3></div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.grafico}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12}} />
                <YAxis hide />
                <Tooltip />
                <Line type="monotone" dataKey="faturamento" stroke="#2563eb" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    if (w.type === 'chart_bar') {
      return (
        <div key={id} className="chart-box full-width animate-pop-in">
          <button className="btn-remove-widget" onClick={() => setWidgetsAtivos(widgetsAtivos.filter(x => x !== id))}><X size={14}/></button>
          <div className="chart-header"><h3><BarChart3 size={18} color="#EA1D2C" /> {w.title}</h3></div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.grafico_pedidos || stats.grafico}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12}} />
                <YAxis hide />
                <Tooltip />
                <Bar dataKey="pedidos" fill="#EA1D2C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    if (w.type === 'chart_bar_produtos') {
      return (
        <div key={id} className="chart-box full-width animate-pop-in">
          <button className="btn-remove-widget" onClick={() => setWidgetsAtivos(widgetsAtivos.filter(x => x !== id))}><X size={14}/></button>
          <div className="chart-header"><h3><Package size={18} color="#16a34a" /> {w.title}</h3></div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.grafico_produtos || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" hide />
                <YAxis dataKey="nome" type="category" width={110} axisLine={false} tickLine={false} tick={{fontSize: 11}} />
                <Tooltip />
                <Bar dataKey="quantidade" fill="#16a34a" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) return <div className="loader-container"><RefreshCw className="spinner" /> <span>Sincronizando BI...</span></div>;

  return (
    <div className="dashboard-dynamic-container">
      <div className="dashboard-header-flex">
        <div>
          <h2 className="content-title">Dashboard Analítico</h2>
          <p className="subtitle">Painel de controle Universo Mídia Digital.</p>
        </div>
        <button className="btn-save-fixed" onClick={() => setShowWidgetMenu(!showWidgetMenu)}>
          <Plus size={18} /> Personalizar Painel
        </button>
      </div>

      {showWidgetMenu && (
        <div className="widget-selector-menu animate-fade-in">
          <h4>Selecione um módulo:</h4>
          <div className="selector-grid">
            {Object.values(templates).map(t => (
              <button key={t.id} onClick={() => { if(!widgetsAtivos.includes(t.id)) setWidgetsAtivos([...widgetsAtivos, t.id]); setShowWidgetMenu(false); }} disabled={widgetsAtivos.includes(t.id)}>
                {t.title}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="widgets-wrapper">
        {widgetsAtivos.map(id => renderWidget(id))}
        <div className="add-widget-placeholder" onClick={() => setShowWidgetMenu(true)}>
          <Plus size={32} />
          <span>Novo Módulo</span>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;